/* dev-false, test-false */
import {
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
  runInInjectionContext,
} from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { NavigationEnd, NavigationExtras, Router, RouterLink } from '@angular/router';
import {
  IonAvatar,
  IonBadge,
  IonButton,
  IonButtons,
  IonCol,
  IonGrid,
  IonHeader,
  IonIcon,
  IonImg,
  IonRow,
  IonToolbar,
  ModalController,
  Platform,
  PopoverController,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  NavController,
} from '@ionic/angular/standalone';
import { onValue, off } from '@angular/fire/database';
import { Subscription } from 'rxjs';

import { AuthService } from 'src/app/services/auth.service';
import { PAGEDATA } from 'src/app/services/constants';
import { DataService } from 'src/app/services/data.service';
import { Events } from 'src/app/services/events.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { AdminGlobalSearchModalComponent } from '../admin-global-search-modal/admin-global-search-modal.component';
import { AddTeamMemberPage } from '../add-team-member/add-team-member.page';
import { SearchComponent } from '../search/search.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgTemplateOutlet,
    RouterLink,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonBadge,
    IonIcon,
    IonAvatar,
    IonImg,
    IonRow,
    IonCol,
    IonGrid,
    IonSegment,
    IonSegmentButton,
    IonLabel,
    SearchComponent,
  ],
})
export class HeaderComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  public authService: AuthService = inject(AuthService);
  public dataService: DataService = inject(DataService);
  public events: Events = inject(Events);
  private platform: Platform = inject(Platform);
  private navCtrl: NavController = inject(NavController);
  private popoverController: PopoverController = inject(PopoverController);
  public router: Router = inject(Router);
  private injector: Injector = inject(Injector);
  private modalCtrl: ModalController = inject(ModalController);
  private notificationsService: NotificationsService = inject(NotificationsService);

  @Input() title: any;
  @Input() userData: any;
  @Input() navigationOpen = false;
  @Output() navigationToggle = new EventEmitter<void>();

  public darkmode: boolean = false;
  public datashow = false;
  public searchUser: any;
  public userId: any;
  public userName: any;
  public users: any;
  public searchingUser: any;
  public blocked: any;
  public imgsrc: any;
  public blockedBy: any;
  public currentUserDetails: any;
  private userCountsUnsub: (() => void) | null = null;
  private badgeSub?: Subscription;
  private adminNotificationsSub?: Subscription;
  public adminUnreadCount: number | null = null;
  public desktop = false;
  public pageName: string = '';
  public param: string | undefined;

  public pageRouteData: any;
  private routerSub?: Subscription;
  private headerDataSub?: Subscription;
  private readonly resizeHandler = () => this.updatePageHeaderHeight();
  private headerResizeObserver?: ResizeObserver;
  private routeContentObserver?: MutationObserver;
  private headerMeasureFrame?: number;
  private scrollBindFrame?: number;
  private scrollBindTimer?: ReturnType<typeof setTimeout>;
  private scrollWriteFrame?: number;
  private scrollContent?: HTMLElement;
  private scrollElement?: HTMLElement;
  private lastScrollTop = 0;
  private collapseProgress = 0;
  private contextCollapsed = false;
  private collapseTransitionUntil = 0;
  private readonly collapseDistance = 72;
  private readonly revealDistance = 28;
  private readonly revealDelta = 8;
  private readonly contentScrollHandler = (event: Event) =>
    this.handleContentScroll(event as CustomEvent<{ scrollTop: number }>);
  private readonly nativeScrollHandler = () => this.handleScrollTop(this.scrollElement?.scrollTop ?? 0);

  private adminHeader?: ElementRef<HTMLElement>;

  @ViewChild('adminHeader', { read: ElementRef })
  private set adminHeaderRef(header: ElementRef<HTMLElement> | undefined) {
    this.adminHeader = header;
    if (!header) return;

    this.observeHeaderHeight();
    this.observeRouteContent();
    this.updatePageHeaderHeight();
    this.bindActivePageScroll();
  }

  ngOnInit() {
    if (!Capacitor.isNativePlatform()) {
      this.desktop = true;
    }
    this.handleNavigation(this.resolveInitialNavigationUrl(this.router.url));
    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.handleNavigation(event.urlAfterRedirects || event.url);
        this.resetContextCollapse();
        this.bindActivePageScroll();
      }
    });
    this.headerDataSub = this.events.currentHeaderData.subscribe((data) => {
      if (!data || data.pageName !== this.pageName) return;
      if ('meta' in data && this.pageRouteData) {
        this.pageRouteData.headerMeta = data.meta || null;
      }
      if (!data?.tab || !this.pageRouteData?.segments) {
        this.updatePageHeaderHeight();
        return;
      }
      if (this.pageRouteData.activeSegment === data.tab) {
        this.updatePageHeaderHeight();
        return;
      }
      this.segmentsCheck(data.tab);
      this.updatePageHeaderHeight();
    });
    window.addEventListener('resize', this.resizeHandler);

    this.darkmode = document.documentElement.classList.contains('ion-palette-dark');

    // Subscribe to badge updates (foreground push) - matching main app pattern
    this.badgeSub = this.events.currentBadge.subscribe(() => {
      this.getUserCounts();
    });

    // Subscribe to admin notifications count (different from user notifications)
    this.adminNotificationsSub = this.notificationsService.counts$.subscribe((counts) => {
      this.adminUnreadCount = counts?.unread ?? null;
      this.updateAdminNotificationBadge();
    });

    // Start listening for user counts if we have buttons
    if (this.pageRouteData?.buttons) {
      this.getCurrentUserDetails();
    }
  }

  ngOnChanges(changes: SimpleChanges = {}) {
    if (this.userData) {
      this.userId = this.userData.uid;
      this.imgsrc = this.userData.photoURL;
    } else {
      runInInjectionContext(this.injector, () => {
        onValue(this.dataService.getCurrentUserdetails(), (account) => {
          if (account.exists()) {
            this.userId = account.val().userId;
            this.imgsrc = account.val().img;
          }
        });
      });
    }

    if (changes['navigationOpen']) {
      this.bindActivePageScroll();
    }
  }

  ngAfterViewInit() {
    this.observeHeaderHeight();
    this.observeRouteContent();
    this.updatePageHeaderHeight();
    this.bindActivePageScroll();
  }

  /** RTDB listener for user details (notificationsCount) - matches main app */
  getCurrentUserDetails() {
    if (this.userCountsUnsub) {
      this.userCountsUnsub();
    }
    runInInjectionContext(this.injector, () => {
      const ref = this.dataService.getCurrentUserdetails();
      this.userCountsUnsub = onValue(ref, async (snapshot) => {
        if (snapshot.exists()) {
          const val = snapshot.val();
          this.currentUserDetails = { key: snapshot.key, ...val };
          await this.getUserCounts();
        }
      });
    });
  }

  /** Update badge counts from RTDB user details - for non-admin-notification buttons */
  async getUserCounts(): Promise<void> {
    if (this.currentUserDetails && this.pageRouteData?.buttons) {
      const val = this.currentUserDetails;
      this.pageRouteData.buttons.forEach((btn: any) => {
        // Admin notifications use separate count from NotificationsService
        if (btn.route === '/admin-notifications') {
          btn.count = this.adminUnreadCount;
        }
        // Add other button counts here if needed (e.g., cart, messages)
      });
    }
  }

  /** Update admin notification badge from NotificationsService */
  updateAdminNotificationBadge(): void {
    if (this.pageRouteData?.buttons) {
      this.pageRouteData.buttons.forEach((btn: any) => {
        if (btn.route === '/admin-notifications') {
          btn.count = this.adminUnreadCount;
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.routerSub) this.routerSub.unsubscribe();
    if (this.headerDataSub) this.headerDataSub.unsubscribe();
    if (this.badgeSub) this.badgeSub.unsubscribe();
    if (this.adminNotificationsSub) this.adminNotificationsSub.unsubscribe();
    if (this.userCountsUnsub) this.userCountsUnsub();
    if (this.headerResizeObserver) this.headerResizeObserver.disconnect();
    if (this.routeContentObserver) this.routeContentObserver.disconnect();
    if (this.headerMeasureFrame) cancelAnimationFrame(this.headerMeasureFrame);
    if (this.scrollBindFrame) cancelAnimationFrame(this.scrollBindFrame);
    if (this.scrollBindTimer) clearTimeout(this.scrollBindTimer);
    if (this.scrollWriteFrame) cancelAnimationFrame(this.scrollWriteFrame);
    this.unbindActivePageScroll();
    window.removeEventListener('resize', this.resizeHandler);
  }

  private bindActivePageScroll(attempt = 0) {
    if (typeof requestAnimationFrame === 'undefined') return;

    if (this.scrollBindFrame) cancelAnimationFrame(this.scrollBindFrame);
    if (this.scrollBindTimer) clearTimeout(this.scrollBindTimer);
    this.scrollBindFrame = requestAnimationFrame(() => {
      this.scrollBindFrame = undefined;

      const pageContents = Array.from(
        document.querySelectorAll<HTMLElement>('ion-router-outlet > .ion-page ion-content')
      );
      const activeContent = [...pageContents]
        .reverse()
        .find((content) => !content.closest('.ion-page-hidden') && content.getClientRects().length > 0);
      const content = activeContent ?? pageContents[pageContents.length - 1];

      if (!content) {
        if (attempt < 10) {
          this.scrollBindTimer = setTimeout(() => this.bindActivePageScroll(attempt + 1), 100);
        }
        return;
      }

      this.unbindActivePageScroll();
      const ionContent = content as HTMLElement & {
        scrollEvents?: boolean;
        getScrollElement?: () => Promise<HTMLElement>;
      };
      if (!ionContent.getScrollElement) {
        if (attempt < 10) {
          this.scrollBindTimer = setTimeout(() => this.bindActivePageScroll(attempt + 1), 100);
        }
        return;
      }

      ionContent.scrollEvents = true;
      this.scrollContent = content;
      content.addEventListener('ionScroll', this.contentScrollHandler, { passive: true });

      void ionContent.getScrollElement().then((scrollElement) => {
        if (this.scrollContent !== content) return;
        this.scrollElement = scrollElement;
        this.lastScrollTop = Math.max(0, scrollElement.scrollTop);
        scrollElement.addEventListener('scroll', this.nativeScrollHandler, { passive: true });
      });
    });
  }

  private observeRouteContent() {
    if (typeof MutationObserver === 'undefined' || this.routeContentObserver) return;

    const routerOutlet = document.querySelector('ion-router-outlet');
    if (!routerOutlet) return;

    this.routeContentObserver = new MutationObserver(() => this.bindActivePageScroll());
    this.routeContentObserver.observe(routerOutlet, { childList: true, subtree: true });
  }

  private unbindActivePageScroll() {
    this.scrollContent?.removeEventListener('ionScroll', this.contentScrollHandler);
    this.scrollElement?.removeEventListener('scroll', this.nativeScrollHandler);
    this.scrollContent = undefined;
    this.scrollElement = undefined;
  }

  private handleContentScroll(event: CustomEvent<{ scrollTop: number }>) {
    this.handleScrollTop(event.detail?.scrollTop ?? 0);
  }

  private handleScrollTop(nextScrollTop: number) {
    const scrollTop = Math.max(0, nextScrollTop);
    const delta = scrollTop - this.lastScrollTop;
    this.lastScrollTop = scrollTop;

    if (delta > 0) {
      this.collapseProgress = Math.min(1, this.collapseProgress + Math.abs(delta) / this.collapseDistance);
    } else if (delta < 0 && this.contextCollapsed && Date.now() < this.collapseTransitionUntil) {
      return;
    } else if (delta < -this.revealDelta || scrollTop <= 4) {
      this.collapseProgress = Math.max(0, this.collapseProgress - Math.abs(delta) / this.revealDistance);
    }

    this.scheduleContextCollapseWrite();
  }

  private scheduleContextCollapseWrite() {
    if (this.scrollWriteFrame) return;

    this.scrollWriteFrame = requestAnimationFrame(() => {
      this.scrollWriteFrame = undefined;
      const shouldCollapse = this.collapseProgress >= 0.98;
      if (shouldCollapse !== this.contextCollapsed) {
        this.contextCollapsed = shouldCollapse;
        this.collapseTransitionUntil = shouldCollapse ? Date.now() + 350 : 0;
      }
      this.adminHeader?.nativeElement.classList.toggle('admin-header--context-collapsed', shouldCollapse);
    });
  }

  private resetContextCollapse() {
    this.lastScrollTop = 0;
    this.collapseProgress = 0;
    this.contextCollapsed = false;
    this.collapseTransitionUntil = 0;
    this.adminHeader?.nativeElement.classList.remove('admin-header--context-collapsed');
  }

  goBack() {
    this.navCtrl.back();
  }

  private handleNavigation(url: string) {
    const cleanUrl = String(url || '')
      .split('?')[0]
      .split('#')[0];
    const routeParts = cleanUrl.split('/').filter(Boolean);
    this.pageName = routeParts[0] || 'dashboard';
    this.param = routeParts[1];
    const routePath = routeParts.join('/');

    const hasRouteParam = routeParts.length > 1;
    const page =
      PAGEDATA.find((item) => this.routeMatches(item.routePath || item.value, routePath)) ||
      PAGEDATA.find((item) => item.value === this.pageName && item.routeHasParam === hasRouteParam) ||
      PAGEDATA.find((item) => item.value === this.pageName);

    if (!page) return;

    this.pageRouteData = page;
    this.pageRouteData.searchBar = this.pageRouteData.baseSearchBar;
    this.pageRouteData.searchBarPh = this.pageRouteData.baseSearchBarPh || this.pageRouteData.searchBarPh || '';
    this.pageRouteData.showFilter = this.pageRouteData.baseShowFilter;
    this.pageRouteData.showDownload = this.pageRouteData.baseShowDownload;
    this.pageRouteData.headerMeta = null;
    this.pageRouteData.loggedInUser = this.param === this.authService.getCurrentAuthUser()?.uid ? true : false;

    if (!this.pageRouteData?.searchBar) {
      this.searchUser = '';
      this.events.updateSearchData(null);
    }

    if (!this.pageRouteData?.segments?.length) {
      delete this.pageRouteData.activeSegment;
    }

    const lastTab = this.pageRouteData?.segments?.length ? this.events.getLastTab(this.pageName) : null;
    if (lastTab) {
      this.pageRouteData.activeSegment = lastTab;
    }

    if (this.pageRouteData?.segments?.length && this.pageRouteData.activeSegment) {
      this.segmentsCheck(this.pageRouteData.activeSegment);
      this.events.updateHeaderData({ tab: this.pageRouteData.activeSegment }, this.pageName);
    }

    if (this.pageRouteData.hideTabs) {
      this.events.publish('hideTabs', { hide: true });
    } else {
      this.events.publish('hideTabs', { hide: false });
    }

    this.updatePageHeaderHeight();
  }

  private resolveInitialNavigationUrl(routerUrl: string, browserPath = globalThis.location?.pathname): string {
    const currentRouterUrl = String(routerUrl || '');
    if (currentRouterUrl && currentRouterUrl !== '/') return currentRouterUrl;

    return String(browserPath || currentRouterUrl || '/');
  }

  private routeMatches(pattern: string, routePath: string): boolean {
    const patternParts = String(pattern || '')
      .split('/')
      .filter(Boolean);
    const routeParts = String(routePath || '')
      .split('/')
      .filter(Boolean);
    if (patternParts.length !== routeParts.length) return false;

    return patternParts.every((part, index) => part.startsWith(':') || part === routeParts[index]);
  }

  private observeHeaderHeight() {
    if (!this.adminHeader?.nativeElement || typeof ResizeObserver === 'undefined') return;

    this.headerResizeObserver?.disconnect();
    this.headerResizeObserver = new ResizeObserver(() => {
      this.updatePageHeaderHeight();
    });
    this.headerResizeObserver.observe(this.adminHeader.nativeElement);
  }

  private updatePageHeaderHeight() {
    if (this.headerMeasureFrame) {
      cancelAnimationFrame(this.headerMeasureFrame);
    }

    this.headerMeasureFrame = requestAnimationFrame(() => {
      const headerElement =
        this.adminHeader?.nativeElement ?? document.querySelector<HTMLElement>('app-header ion-header.admin-header');
      const measuredHeight = headerElement?.getBoundingClientRect().height;
      const fallbackHeight = window.innerWidth < 768 ? 156 : 132;
      const height = measuredHeight && measuredHeight > 0 ? Math.ceil(measuredHeight) : fallbackHeight;

      document.documentElement.style.setProperty('--admin-page-header-height', `${height}px`);
    });
  }

  private segmentsCheck(value: string) {
    const currentSegment = this.pageRouteData?.segments?.find((segment: any) => segment.value === value);
    this.pageRouteData.searchBar =
      currentSegment?.searchBar === undefined ? this.pageRouteData.baseSearchBar : currentSegment.searchBar;
    this.pageRouteData.showFilter =
      currentSegment?.showFilter === undefined ? this.pageRouteData.baseShowFilter : currentSegment.showFilter;
    this.pageRouteData.showDownload =
      currentSegment?.showDownload === undefined ? this.pageRouteData.baseShowDownload : currentSegment.showDownload;
    this.pageRouteData.searchBarPh = currentSegment?.searchBarPh || this.pageRouteData.baseSearchBarPh || '';
    if (!this.pageRouteData.searchBar) {
      this.searchUser = '';
      this.events.updateSearchData(null);
    }
    this.pageRouteData.activeSegment = value;
  }

  segmentChanged(ev: any) {
    const value = ev?.detail?.value;
    if (!value) return;
    this.segmentsCheck(value);
    this.events.updateHeaderData({ tab: value }, this.pageName);
    this.updatePageHeaderHeight();
  }

  onSearchInput(event: any) {
    const value = (event?.detail?.value ?? '').toString();
    this.searchUser = value;
    this.events.updateSearchData({
      string: value,
      page: this.pageName,
      segment: this.events.getLastTab(this.pageName),
    });
  }

  addsearchItem(newsearchData: any) {
    this.users.push(newsearchData);
  }

  addsearchString(searchString: any) {
    this.searchUser = searchString;
  }

  //View User
  viewUser(userId: any) {
    const loggedInUserId: any = this.authService.getCurrentAuthUser()?.uid;
    const id = userId;
    if (loggedInUserId === userId) {
      const navigationExtras: NavigationExtras = {
        state: {
          user: true,
          userdata: '',
        },
      };
      this.router.navigate(['profile', id], navigationExtras);
    } else {
      this.router.navigate(['user-info', id]);
    }
  }

  notifications() {
    this.router.navigate(['notifications']);
  }

  tools() {
    this.router.navigate(['tools']);
  }

  loadDefault(event: any) {
    event.target.src = '/assets/imgs/default-dp.png';
  }

  goHome() {
    this.router.navigate(['dashboard']);
  }

  opencamera() {
    const navigationExtras: NavigationExtras = {
      state: {
        userName: this.userName,
      },
    };
    this.router.navigate(['qrcode'], navigationExtras);
  }

  opensettings() {
    this.router.navigate(['settings']);
  }

  changedarkMode() {
    const isDark = document.documentElement.classList.contains('ion-palette-dark');
    const newMode = !isDark;

    document.documentElement.classList.toggle('ion-palette-dark', newMode);
    localStorage.setItem('darkmode', newMode ? 'true' : 'false');
    this.darkmode = newMode;
  }

  async openGlobalSearch() {
    const modal = await this.modalCtrl.create({
      component: AdminGlobalSearchModalComponent,
      cssClass: 'admin-global-search-modal',
    });
    await modal.present();
  }

  async openOptionButton() {
    if (!this.pageRouteData?.value) return;

    switch (this.pageRouteData.value) {
      case 'teammembers':
        await this.openAddTeamMember();
        break;
      default:
        break;
    }
  }

  private async openAddTeamMember() {
    const modal = await this.modalCtrl.create({
      component: AddTeamMemberPage,
      componentProps: { pageType: 'Add', userId: '' },
    });
    await modal.present();
  }
}
